import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import "./index.less";
import { Avatar, List, Popover } from "antd";
import TencentCloudChat, { Message } from "@tencentcloud/chat";
import { MsgContent, extensionProps } from "./GroupMsg";
import { TitleCom } from "./TitleCom";
import { useChannelRoomStore, useChatStore, useFriendStore } from "@obsbot/remo-stores";
import { UserOutlined } from "@ant-design/icons";
import { ICustomPayloadDescription } from "@obsbot/remo-services";
import { ChatType } from "../ChatSendInput";
import { eventBus } from "@obsbot/remo-utils";
import { useParams } from "react-router-dom";
import { debounce } from "lodash-es"; // 引入防抖函数
import { usePushHistory } from "@obsbot/remo-pages/src/utils/routes";
import { useTranslate } from "@obsbot/remo-i18n";
import { setIntimateChatRead } from "@obsbot/remo-server-api";
import { HoverMenu } from "./HoverMenu";
// import useWebSocketStore from "@obsbot/remo-stores/src/ws-store";

export interface IMsgItem extends Message {
    extension?: extensionProps[];
}

export interface MsgDataProps {
    hasMore: boolean;
    chatData: IMsgItem[];
    nextReqMessageID: string;
}
export interface ChatMsgProps {
    count?: number; // 分页查询的条数
    conversationID: string; // 会话ID
    referenceMessage?: IMsgItem | null;
    setReferenceMessage?: (message: IMsgItem | null) => void;
    hoverWindow?: boolean;
    chatType?: ChatType;
    tempMsg?: any | null;
}
export const ChatMsg: React.FC<ChatMsgProps> = ({
    count = 2,
    tempMsg,
    conversationID,
    referenceMessage,
    setReferenceMessage,
    hoverWindow = false,
    chatType = ChatType.Chat,
}) => {
    const listContainerRef = useRef<HTMLDivElement>(null);
    const params = useParams();
    const t = useTranslate();
    const { rtm } = useChatStore();
    const defaultData = {
        hasMore: false,
        chatData: [],
        nextReqMessageID: "",
    };
    const [msgData, setMsgData] = useState<MsgDataProps>(defaultData);
    const isLoadingMore = useRef(false);
    const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
    const isLoadingRef = useRef(false);
    const previousScrollHeightRef = useRef(0);
    const { blockedUsers } = useChannelRoomStore();
    const [showMsgHover, setShowMsgHover] = useState<string>();
    // const { sendMessage, onMessage } = useWebSocketStore();

    const shouldScrollToBottomRef = useRef(shouldScrollToBottom);
    useEffect(() => {
        shouldScrollToBottomRef.current = shouldScrollToBottom;
    }, [shouldScrollToBottom]);

    // 处理接收到消息后滚动到底部
    useEffect(() => {
        // 组件展开就滚动到底部
        setTimeout(() => {
            debouncedScrollToBottom();
        }, 0);
        eventBus.on("message-received", debouncedScrollToBottom);
        return () => {
            eventBus.off("message-received", debouncedScrollToBottom);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 防抖的滚动到底部函数
    const debouncedScrollToBottom = useCallback(
        debounce(() => {
            const container = listContainerRef.current;
            if (container && shouldScrollToBottomRef.current) {
                const attemptScroll = (retries = 3): void => {
                    if (retries <= 0) {
                        return;
                    }
                    const lastMessage = container.lastElementChild;
                    if (lastMessage) {
                        lastMessage.scrollIntoView({
                            behavior: "smooth",
                            block: "end",
                            inline: "nearest",
                        });
                    }
                    container.scrollTop = container.scrollHeight;
                    // 延迟一小段时间后再次检查是否真的在底部
                    setTimeout(() => {
                        const isAtBottom =
                            container.scrollHeight - container.scrollTop - container.clientHeight <
                            10; // 更小的容差
                        if (!isAtBottom && shouldScrollToBottomRef.current) {
                            // console.log(`Retry scrolling, attempt: ${retries}`);
                            attemptScroll(retries - 1);
                        }
                    }, 50); // 50ms 延迟
                };
                attemptScroll();
            }
        }, 300),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    ); // 增加 debounce 延迟

    const pushHistory = usePushHistory();
    useEffect(() => {
        getMessageList([]);
        return () => {
            setShouldScrollToBottom(true);
            debouncedScrollToBottom.cancel(); // 组件卸载时取消防抖
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count, conversationID]);

    useEffect(() => {
        const handleSendMsg = async (eventData: {
            userID: string;
            conversationID?: string;
        }): Promise<void> => {
            const tempConversionID =
                chatType === ChatType.Chat
                    ? "C2C" + eventData.userID
                    : chatType === ChatType.Channel
                      ? eventData.conversationID
                      : eventData.userID;
            if (rtm && conversationID === tempConversionID) {
                getMessageList([]);
                rtm.setMessageRead(conversationID);
                debouncedScrollToBottom();
            }
        };
        eventBus.on("send-new-msg", handleSendMsg);
        return () => {
            eventBus.off("send-new-msg", handleSendMsg);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatType, conversationID]);

    useEffect(() => {
        const handleReceivedMsg = (message: any): void => {
            if (message.conversationID === conversationID) {
                getMessageList([]);
                rtm?.setMessageRead(conversationID);
                setShouldScrollToBottom(true); // 新消息来了，应该滚动到底部
            }
            const intimate = useFriendStore.getState().intimate;
            if (intimate.unreadCount > 0) {
                const sendsUser = intimate.receiveList.find(
                    item => item.from.userID === message.from,
                );
                sendsUser && setIntimateChatRead(message.from);
            }
        };
        rtm?.events.on("message-received", handleReceivedMsg);

        return () => {
            rtm?.events.off("message-received", handleReceivedMsg);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.id, conversationID]);

    // MutationObserver 设置
    useEffect(() => {
        const container = listContainerRef.current;
        if (!container) {
            return;
        }

        const observer = new MutationObserver(mutationsList => {
            // 对于每次检测到的变化，都尝试调用防抖的滚动函数
            // 防抖函数内部会处理是否真的执行滚动
            for (const mutation of mutationsList) {
                if (mutation.type === "childList" || mutation.type === "characterData") {
                    // console.log('DOM changed, attempting to scroll to bottom');
                    debouncedScrollToBottom();
                    break; // 一旦检测到相关变化，就调用一次，防抖会处理后续
                }
            }
        });

        observer.observe(container, {
            childList: true, // 观察直接子节点的变动
            subtree: true, // 观察所有后代节点的变动
            attributes: true, // 观察属性变动 (例如 style, class)
            attributeFilter: ["style", "class"], // 可以指定观察哪些属性
            characterData: true, // 观察文本节点内容变化
        });

        return () => {
            observer.disconnect();
            debouncedScrollToBottom.cancel();
        };
    }, [debouncedScrollToBottom]); // 依赖 debouncedScrollToBottom

    // 初始加载和消息列表变化时的滚动逻辑
    useLayoutEffect(() => {
        const container = listContainerRef.current;
        if (!container) {
            return;
        }

        if (isLoadingMore.current) {
            const newScrollHeight = container.scrollHeight;
            const scrollDiff = newScrollHeight - previousScrollHeightRef.current;
            if (scrollDiff > 0) {
                container.scrollTop = scrollDiff;
            }
            requestAnimationFrame(() => {
                isLoadingMore.current = false;
                previousScrollHeightRef.current = 0;
            });
        } else if (shouldScrollToBottom) {
            // 对于初次加载或明确需要滚动的情况，可以立即尝试一次
            // MutationObserver 之后会进行更精确的调整
            requestAnimationFrame(() => {
                const lastMessage = container.lastElementChild;
                if (lastMessage) {
                    lastMessage.scrollIntoView({
                        behavior: "smooth",
                        block: "end",
                        inline: "nearest",
                    });
                }
                container.scrollTop = container.scrollHeight;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [msgData.chatData]); // 依赖消息数据，当数据变化时触发

    const getMessageList = async (
        curChatData: IMsgItem[],
        nextReqMessageID?: string,
    ): Promise<void> => {
        if (rtm && conversationID) {
            const res = await rtm.getMessageList(conversationID, nextReqMessageID);
            let tempResMsg = [...res.messageList];
            if (hoverWindow && blockedUsers.length > 0) {
                // 屏蔽群聊中被当前用户屏蔽的成员的消息
                tempResMsg = tempResMsg.filter(item => !blockedUsers.includes(item.from));
            }
            const tempMsg = tempResMsg.filter(
                message =>
                    message.payload?.description !== ICustomPayloadDescription.ChannelCommand &&
                    message.payload?.description !== "" &&
                    !message.isDeleted,
            );
            setMsgData({
                hasMore: !res.isCompleted,
                chatData: [...tempMsg, ...curChatData],
                nextReqMessageID: res.nextReqMessageID,
            });
        }
    };
    // 滚动事件处理函数
    const handleScroll = async (): Promise<void> => {
        if (listContainerRef.current && !isLoadingRef.current) {
            const { scrollTop, scrollHeight } = listContainerRef.current;
            // 判断是否滚动到顶部，并检查是否还有更多数据且当前不在加载中
            if (scrollTop === 0 && msgData.hasMore) {
                isLoadingRef.current = true;
                isLoadingMore.current = true;
                // 记录当前滚动高度，加载完成后尝试保持位置
                previousScrollHeightRef.current = scrollHeight;
                try {
                    await getMessageList(msgData.chatData, msgData.nextReqMessageID);
                } catch (error) {
                    console.error("加载更多消息失败:", error);
                } finally {
                    isLoadingRef.current = false;
                }
            }
        }
    };

    const clearMessageList = (eventData: { userID: string }): void => {
        if (eventData.userID === params.id) {
            getMessageList([]);
        }
    };
    const handleScrollSearchMsg = async ({
        msgID,
        msgData: curMsgData,
    }: {
        msgID: string;
        msgData: any;
    }): Promise<void> => {
        console.log("[WEB DEBUG]滚动");
        const container = listContainerRef.current;
        if (!container) {
            return;
        }

        // 使用容器内部的滚动而不是window.scroll
        const scrollToElement = (element: HTMLElement): void => {
            const containerRect = container.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();

            // 计算相对于容器的位置
            const scrollTop = elementRect.top - containerRect.top + container.scrollTop;

            // 平滑滚动到目标位置
            container.scrollTo({
                top: scrollTop - container.clientHeight / 2,
                behavior: "smooth",
            });
        };

        // 检查消息是否已加载
        const messageElement = document.getElementById(msgID);
        if (messageElement) {
            scrollToElement(messageElement);
        } else {
            // 消息未加载，需要先加载数据
            isLoadingRef.current = true;
            try {
                // 加载更多消息
                setMsgData(curMsgData as MsgDataProps);
                // await getMessageList(msgData.chatData, msgData.nextReqMessageID);
                // 检查消息是否已加载
                const newMessageElement = document.getElementById(msgID);
                if (newMessageElement) {
                    scrollToElement(newMessageElement);
                }
            } catch (error) {
                eventBus.emit("show-message", {
                    message: t("chat.search-msg-loading"),
                    type: "warning",
                });
            } finally {
                isLoadingRef.current = false;
            }
        }
    };

    useEffect(() => {
        eventBus.on("scroll-to-msg", handleScrollSearchMsg);
        eventBus.on("clear-history-msg", clearMessageList);
        return () => {
            eventBus.off("clear-history-msg", clearMessageList);
            eventBus.off("scroll-to-msg", handleScrollSearchMsg);
        };
    }, []);

    useEffect(() => {
        setMsgData({
            ...msgData,
            chatData: tempMsg ? [...msgData.chatData, tempMsg] : msgData.chatData,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tempMsg]);

    const updateMsgList = async (msg: Message): Promise<void> => {
        let newMsgData = [];
        newMsgData = msgData.chatData.filter(item => item.ID !== msg.ID);
        setMsgData({
            ...msgData,
            chatData: [...newMsgData],
        });
    };

    // 处理选择引用某条消息
    const handleReferenceMessage = (messageId: string): void => {
        const message = msgData.chatData.find(msg => msg.ID === messageId);
        if (!message) {
            eventBus.emit("show-message", {
                message: t("chat.reference-message-not-exist"),
                type: "warning",
            });
            return;
        } else if (message?.status !== "success") {
            eventBus.emit("show-message", {
                message: t("chat.reference-message-not-success"),
                type: "warning",
            });
            return;
        }
        setReferenceMessage && setReferenceMessage(message || null);
    };

    return (
        <div
            ref={listContainerRef}
            className={"chat-msg" + (chatType === ChatType.Channel ? " channel-chat-msg" : "")}
            style={{
                height:
                    chatType === ChatType.Channel
                        ? undefined
                        : referenceMessage
                          ? "calc(100% - 230px)"
                          : "calc(100% - 168px)",
                maxHeight:
                    chatType === ChatType.Channel
                        ? "unset"
                        : referenceMessage
                          ? "calc(100% - 230px)"
                          : "calc(100% - 168px)",
            }}
            onScroll={handleScroll} // 用于加载更多
            onWheel={() => setShouldScrollToBottom(false)} // 监听滚轮事件判断用户是否手动滚动
            // onTouchMove={handleManualScroll} // 监听触摸事件
        >
            <List
                dataSource={msgData.chatData}
                locale={{
                    emptyText:
                        chatType === ChatType.Channel
                            ? t("channel.no-channel-message")
                            : t("chat.no-message"),
                }}
                renderItem={item =>
                    item.payload?.description === ICustomPayloadDescription.ChannelCommand ||
                    item.payload?.description === "" ? null : (
                        <List.Item
                            key={item.sequence}
                            className={`item-type-${item.type} chat-flow-${item.flow}`}
                            id={item.ID}
                            onMouseEnter={() => setShowMsgHover(item.ID)}
                            onMouseLeave={() => setShowMsgHover("")}
                        >
                            <Popover
                                // 群组系统通知和群组提示不显示菜单
                                align={{ offset: [0, 8] }}
                                arrow={false}
                                classNames={{
                                    root: "msg-content-hover-menu",
                                    body: "inner-popover-style",
                                }}
                                content={
                                    rtm &&
                                    chatType === ChatType.Chat && (
                                        <HoverMenu
                                            msg={item}
                                            rtm={rtm}
                                            updateMsgList={updateMsgList}
                                            onReferenceMessage={handleReferenceMessage}
                                        />
                                    )
                                }
                                open={
                                    hoverWindow ||
                                    item.type === TencentCloudChat.TYPES.MSG_GRP_SYS_NOTICE ||
                                    item.type === TencentCloudChat.TYPES.MSG_GRP_TIP
                                        ? false
                                        : showMsgHover === item.ID
                                }
                                placement={item.flow === "in" ? "topRight" : "topLeft"}
                            >
                                <List.Item.Meta
                                    avatar={
                                        // 群组系统通知和群组提示不显示头像
                                        item.type !== TencentCloudChat.TYPES.MSG_GRP_SYS_NOTICE &&
                                        item.type !== TencentCloudChat.TYPES.MSG_GRP_TIP ? (
                                            <Avatar
                                                className="chat-msg-avatar"
                                                size={chatType === ChatType.Channel ? 30 : 40}
                                                src={
                                                    item.avatar || (
                                                        <UserOutlined
                                                            style={{ fontSize: 24 }}
                                                            onPointerMoveCapture={undefined}
                                                            onPointerOverCapture={undefined}
                                                        />
                                                    )
                                                }
                                            />
                                        ) : null
                                    }
                                    description={
                                        item.isRevoked ? (
                                            <div className={"revoked-msg flow-" + item.flow}>
                                                {t("chat.revoked-msg")}
                                            </div>
                                        ) : (
                                            <MsgContent
                                                key={item.ID}
                                                basicInfo={{
                                                    from: item.from,
                                                    fromNick: item.nick,
                                                    to: item.to,
                                                    flow: item.flow,
                                                }}
                                                hoverWindow={hoverWindow}
                                                msg={item}
                                                msgData={msgData}
                                                pushHistory={pushHistory}
                                            />
                                        )
                                    }
                                    title={
                                        // 群组系统通知和群组提示不显示用户
                                        item.type !== TencentCloudChat.TYPES.MSG_GRP_SYS_NOTICE &&
                                        item.type !== TencentCloudChat.TYPES.MSG_GRP_TIP ? (
                                            <TitleCom
                                                isShowName={chatType === ChatType.Channel}
                                                sendTime={item.time}
                                                userName={item.nick || item.from}
                                            />
                                        ) : null
                                    }
                                />
                            </Popover>
                        </List.Item>
                    )
                }
            />
        </div>
    );
};
